import { useEffect, useRef, useState } from "react";

const SLICES = 30;
const URLS = Array.from({ length: SLICES }, (_, i) => `/assets/mri/slice_${String(i).padStart(2, "0")}.webp`);

const MriHero = () => {
  const [pos, setPos] = useState(SLICES * 0.55);
  const [probe, setProbe] = useState(null);
  const [isTouch, setIsTouch] = useState(false);
  const target = useRef(SLICES * 0.55);
  const frameRef = useRef(null);

  useEffect(() => {
    setIsTouch(window.matchMedia("(pointer: coarse)").matches);
  }, []);

  // One-time gentle sweep on first touch-device visit so visitors discover the scrubber
  useEffect(() => {
    if (!isTouch) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    try {
      if (localStorage.getItem("qelp_mri_tour")) return;
    } catch {
      return;
    }
    const start = target.current;
    const DURATION = 2600;
    let raf;
    let cancelled = false;
    const markSeen = () => {
      try { localStorage.setItem("qelp_mri_tour", "1"); } catch { /* private mode */ }
    };
    const cancel = () => {
      if (!cancelled) {
        cancelled = true;
        markSeen();
      }
    };
    window.addEventListener("touchstart", cancel, { once: true, passive: true });
    const animate = (now) => {
      if (cancelled) return;
      const t = Math.min(1, (now - t0) / DURATION);
      target.current = start + Math.sin(t * Math.PI) * SLICES * 0.33;
      if (t < 1) {
        raf = requestAnimationFrame(animate);
      } else {
        target.current = start;
        markSeen();
      }
    };
    const t0 = performance.now();
    const timer = setTimeout(() => {
      raf = requestAnimationFrame(animate);
    }, 1400);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      window.removeEventListener("touchstart", cancel);
    };
  }, [isTouch]);

  useEffect(() => {
    URLS.forEach((u) => {
      const im = new Image();
      im.src = u;
    });
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    const tick = () => {
      setPos((prev) => {
        if (reduced) return target.current;
        const next = prev + (target.current - prev) * 0.06;
        return Math.abs(next - prev) < 0.001 ? prev : next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onMove = (clientX, clientY) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (clientX - rect.left) / rect.width;
    const py = (clientY - rect.top) / rect.height;
    if (px < 0 || px > 1 || py < 0 || py > 1) {
      setProbe(null);
      return;
    }
    target.current = Math.min(SLICES - 1, Math.max(0, px * (SLICES - 1)));
    setProbe({ x: px, y: py });
  };

  const onTouch = (e) => {
    const t = e.touches[0];
    if (t) onMove(t.clientX, t.clientY);
  };

  const lo = Math.floor(pos);
  const hi = Math.min(SLICES - 1, lo + 1);
  const frac = pos - lo;
  const idx = Math.round(pos);
  const zmm = Math.round((idx / (SLICES - 1)) * 120 - 60);
  const signal = probe ? 0.3 + 0.65 * Math.abs(Math.sin(probe.x * 8.2 + probe.y * 5.1 + pos * 0.7)) : 0;

  return (
    <div
      ref={frameRef}
      onMouseMove={(e) => onMove(e.clientX, e.clientY)}
      onMouseLeave={() => setProbe(null)}
      onTouchStart={onTouch}
      onTouchMove={onTouch}
      onTouchEnd={() => setProbe(null)}
      className="absolute right-0 md:right-[4vw] top-1/2 -translate-y-1/2 w-[94vw] sm:w-[54vw] md:w-[44vw] max-w-[600px] aspect-[5/6] opacity-60 md:opacity-100 cursor-crosshair select-none"
      style={{
        maskImage: "radial-gradient(ellipse at center, black 52%, transparent 76%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 52%, transparent 76%)",
        touchAction: "pan-y",
        WebkitTouchCallout: "none",
      }}
      data-testid="mri-hero"
      aria-hidden="true"
    >
      {URLS.map((u, i) => (
        <img
          key={u}
          src={u}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full object-contain mix-blend-screen pointer-events-none"
          style={{ opacity: i === lo ? 1 - frac : i === hi ? frac : 0 }}
        />
      ))}

      <div className="absolute inset-[6%] pointer-events-none">
        <span className="absolute top-0 left-0 w-5 h-5 border-l border-t border-[#10B8B1]/50" />
        <span className="absolute top-0 right-0 w-5 h-5 border-r border-t border-[#10B8B1]/50" />
        <span className="absolute bottom-0 left-0 w-5 h-5 border-l border-b border-[#10B8B1]/50" />
        <span className="absolute bottom-0 right-0 w-5 h-5 border-r border-b border-[#10B8B1]/50" />
      </div>

      {probe && (
        <>
          <div className="absolute top-[6%] bottom-[6%] w-px bg-[#10B8B1]/40 pointer-events-none" style={{ left: `${probe.x * 100}%` }} />
          <div className="absolute left-[6%] right-[6%] h-px bg-[#10B8B1]/40 pointer-events-none" style={{ top: `${probe.y * 100}%` }} />
          <div
            className="absolute pointer-events-none rounded-md border border-[#10B8B1]/30 bg-[#050422]/85 backdrop-blur-sm px-2.5 py-1.5 font-mono-qelp text-[9px] tracking-[0.15em] uppercase text-[#10B8B1]"
            style={{ left: `${Math.min(probe.x * 100 + 2, 62)}%`, top: `${Math.min(probe.y * 100 + 2, 86)}%` }}
            data-testid="mri-probe-readout"
          >
            Signal {signal.toFixed(2)}
          </div>
        </>
      )}

      <div className="absolute left-[10%] top-[7%] pointer-events-none font-mono-qelp text-[9px] tracking-[0.25em] uppercase text-slate-600" data-testid="mri-hint">
        {isTouch ? "Drag to slice" : "Hover to slice"}
      </div>

      <div className="absolute left-[8%] right-[8%] bottom-[7%] pointer-events-none" data-testid="mri-slice-readout">
        <div className="flex justify-between font-mono-qelp text-[9px] tracking-[0.2em] uppercase text-slate-500">
          <span>Axial · T1 · ICBM152</span>
          <span className="text-[#10B8B1]">Slice {String(idx + 1).padStart(2, "0")}/{SLICES} · Z {zmm >= 0 ? `+${zmm}` : zmm}mm</span>
        </div>
        <div className="mt-2 h-px bg-white/10 relative">
          <div className="absolute inset-y-0 left-0 bg-[#10B8B1]" style={{ width: `${(pos / (SLICES - 1)) * 100}%` }} />
        </div>
      </div>
    </div>
  );
};

export default MriHero;
